/**
 * @file src/_modulos/prenota/tabela/layouts/filters/TabStatusIndicator.tsx
 * @description Componente simplificado de indicador de status
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas mostra status
 * - YAGNI: Remove animações complexas não utilizadas
 */

"use client";

import {
  IconClock,
  IconRefresh,
  IconCheck,
  IconAlertCircle,
  IconLoader,
} from "@tabler/icons-react";
import { Badge, cn } from "visuais";

interface TableStatusIndicatorProps {
  isLoading: boolean;
  isStale: boolean;
  hasCachedData: boolean;
  lastUpdated?: Date;
  error?: string;
  onRefresh?: () => void;
  tabColor?: string;
}

export function TableStatusIndicator({
  isLoading,
  isStale,
  hasCachedData,
  lastUpdated,
  error,
  onRefresh,
}: TableStatusIndicatorProps) {
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return null;
    if (diffMinutes < 60) return `${diffMinutes}m`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const getStatusIcon = () => {
    if (error) return IconAlertCircle;
    if (isLoading) return IconLoader;
    if (isStale) return IconClock;
    if (hasCachedData) return IconRefresh;
    return IconCheck;
  };

  const getStatusText = () => {
    if (error) return "Erro";
    if (isLoading) return "Atualizando";
    if (isStale) return "Desatualizado";
    if (hasCachedData) return "Cache";
    return "";
  };

  const getStatusColor = () => {
    if (error)
      return "text-destructive border-destructive/20 bg-destructive/10";
    if (isLoading) return "text-blue-500 border-blue-500/20 bg-blue-500/10";
    if (isStale) return "text-orange-500 border-orange-500/20 bg-orange-500/10";
    if (hasCachedData)
      return "text-purple-500 border-purple-500/20 bg-purple-500/10";
    return "text-green-500 border-green-500/20 bg-green-500/10";
  };

  const StatusIcon = getStatusIcon();
  const timeAgo = lastUpdated ? formatTimeAgo(lastUpdated) : null;

  return (
    <div className="flex items-center gap-2 text-xs">
      <Badge
        variant="outline"
        className={cn(
          getStatusColor(),
          "flex items-center gap-1.5 cursor-pointer transition-all duration-200",
          onRefresh && !isLoading && "hover:bg-accent/50"
        )}
        onClick={onRefresh && !isLoading ? onRefresh : undefined}
      >
        <StatusIcon className="h-3.5 w-3.5" />

        {/* Mostra tempo apenas se não for loading/erro */}
        {timeAgo && !isLoading && !error && (
          <span className="text-muted-foreground">{timeAgo}</span>
        )}
      </Badge>
    </div>
  );
}
